import { plural } from '../format';

/**
 * The connections area's words (console redesign, docs/console-redesign-plan.md).
 *
 * One file per area so the redesign's parallel work never edits the same
 * dictionary. `en.ts` and `es.ts` spread these in; the Spanish half is typed
 * against the English one, so a key missing here is a build failure exactly as
 * it is in the main catalog.
 *
 * The rule this tab's copy follows (plan §1, principle 2): no wire tokens in a
 * sentence. A scope is said as what it lets a partner do, an event as what
 * happened at the business, a delivery status as what became of the update.
 * The tokens themselves still appear where an operator copies them — a key's
 * prefix, an address, a delivery's id — in mono, never inside the words.
 */
export const connectionsEn = {
  /* ---- the tab ---------------------------------------------------- */
  'connections.fence':
    'Connections belong to the second stage, taking bookings online. The business doesn’t need any of them to run its day in the app.',
  'connections.loading': 'the concierge',

  /* ---- the concierge ---------------------------------------------- */
  'connections.concierge.title': 'AI concierge',
  'connections.concierge.chip.unavailable': 'Not available here',
  'connections.concierge.chip.off': 'Not connected',
  'connections.concierge.chip.paused': 'Paused',
  'connections.concierge.chip.broken': 'Can’t reach the book',
  'connections.concierge.chip.todo': ({ count }: { count: number }) =>
    plural(count, '1 thing to do', `${String(count)} things to do`),
  'connections.concierge.chip.on': 'Answering',
  'connections.concierge.chip.unknown': 'Didn’t answer',
  'connections.concierge.unavailable':
    'This console isn’t linked to the concierge service, so it can’t connect anyone from here. Nothing is wrong with the business.',
  'connections.concierge.unavailableWhat': 'What this deployment is missing',
  'connections.concierge.unreachable': ({ message }: { message: string }) =>
    `The concierge didn’t answer (${message}). What’s shown here is this side only.`,
  'connections.concierge.pitch': 'Answers their clients and books for them. Before it can go on:',
  'connections.concierge.todo.agree':
    'The owner agrees an assistant may answer in their name, and on which number',
  'connections.concierge.todo.services': 'Choose which services it may book',
  'connections.concierge.todo.phone': 'A phone number for it',
  'connections.concierge.connect': 'Connect the concierge…',
  'connections.concierge.connectTitle': ({ name }: { name: string }) =>
    `Connect the concierge to ${name}?`,
  'connections.concierge.connectConsequence':
    'Turns on the client book, the service list, the calendar and partner access if they are off; makes the concierge its own key and a secret for signing updates, and hands both over machine to machine — neither ever reaches this browser; then sends it the services and the business details. It books nothing until the owner has chosen which services it may book.',
  'connections.concierge.connectLabel': 'Connect',
  'connections.concierge.connected':
    'Connected. Its key and its signing secret went machine to machine — neither reached this browser.',

  'connections.concierge.doing.on': ({ name }: { name: string }) =>
    `Answering ${name}’s clients and booking for them.`,
  'connections.concierge.doing.paused':
    'Paused: it still answers and takes messages, but books nothing until it is resumed.',
  'connections.concierge.doing.broken':
    'It can’t use the key it holds, so it can’t see openings or book. Re-key and re-sync.',
  'connections.concierge.doing.brokenDetail': ({ detail }: { detail: string }) =>
    `What it said: ${detail}`,
  'connections.concierge.synced': ({ services, details }: { services: string; details: string }) =>
    `Services last sent ${services}; business details last sent ${details}.`,
  'connections.concierge.never': 'never',
  'connections.concierge.syncNote':
    'Normally there is no need to sync by hand — a change to the services or the business details reaches it within seconds.',
  'connections.concierge.stillTodo': 'Still to do',
  'connections.concierge.missing.provider_connection':
    'It can’t use the key it holds — re-key and re-sync.',
  'connections.concierge.missing.catalog':
    'Nothing bookable is published yet — add services first.',
  'connections.concierge.missing.phone_number':
    'No phone number yet: chat works, calls and texts don’t.',
  'connections.concierge.missing.owner_number':
    'Nobody to text when it needs help — set the owner’s number in “What the agent does”.',
  'connections.concierge.openConsole': 'Open the concierge’s console',

  'connections.concierge.wiring': 'Its key and update address',
  'connections.concierge.itsKeys': 'Its key',
  'connections.concierge.noKey': 'It holds no working key — re-key and re-sync.',
  'connections.concierge.updatesTo': 'Booking updates go to',
  'connections.concierge.noAddress': 'Nowhere yet — re-key and re-sync sets it.',
  'connections.concierge.addressFailing':
    'Updates to it are failing — see “Booking updates to partners”.',
  'connections.concierge.addressStopped': 'Sending is stopped.',
  'connections.concierge.secretAt': 'Signing secret kept at',
  'connections.concierge.siteKey': 'Chat widget key for the website',
  'connections.concierge.siteKeyNote': 'Public — it goes in the website’s code. Not a secret.',

  'connections.concierge.sync': 'Sync now',
  'connections.concierge.synced.done': 'Sent again.',
  'connections.concierge.pause': 'Pause…',
  'connections.concierge.resume': 'Resume',
  'connections.concierge.resumed': 'Resumed — it books again.',
  'connections.concierge.pauseTitle': 'Pause the concierge?',
  'connections.concierge.pauseConsequence': ({ name }: { name: string }) =>
    `It keeps answering ${name}’s clients and takes messages, but books nothing until it is resumed. Its key and its settings stay as they are.`,
  'connections.concierge.pauseLabel': 'Pause',
  'connections.concierge.rekey': 'Re-key and re-sync…',
  'connections.concierge.rekeyTitle': 'Re-key and re-sync the concierge?',
  'connections.concierge.rekeyConsequence':
    'A fresh key is made and handed over machine to machine. The previous one is revoked once the concierge can use the new one — if it can’t, the old key is kept and this card says so. The services and business details are sent again. Nothing is deleted, and clients notice nothing.',
  'connections.concierge.rekeyLabel': 'Re-key and re-sync',
  'connections.concierge.revoke': 'Pause and revoke…',
  'connections.concierge.revokeTitle': 'Pause the concierge and revoke its key?',
  'connections.concierge.revokeConsequence':
    'It keeps answering and takes messages, but books nothing. Its key is revoked and partner access is turned off, so it can no longer read the business’s services or openings. Nothing is deleted — not the number, the conversations or the settings — and connecting again makes a fresh key.',
  'connections.concierge.revokeLabel': 'Pause and revoke',
  'connections.concierge.revoked': 'Paused, and its key is revoked.',

  /* ---- what the agent does ---------------------------------------- */
  'connections.agent.title': 'What the agent does',
  'connections.agent.edit': 'Change what the agent does…',
  'connections.agent.note':
    'What it knows — hours, prices, policies — comes from the business’s own setup and is changed there. This is only how it behaves.',
  'connections.agent.autonomy': 'How much it may do on its own',
  'connections.agent.autonomy.conservative': 'Asks first',
  'connections.agent.autonomy.conservative.what':
    'Answers and gathers the details, but doesn’t book: it proposes, the owner confirms.',
  'connections.agent.autonomy.balanced': 'Books, and flags the unusual',
  'connections.agent.autonomy.balanced.what':
    'Books any service it may book, and hands anything custom, discounted or complaint-shaped to the owner with a drafted reply.',
  'connections.agent.autonomy.autonomous': 'Books on its own',
  'connections.agent.autonomy.autonomous.what':
    'Books without checking first, and only hands over what is custom or above the amount below.',
  'connections.agent.autonomy.notSet': 'Not chosen yet.',
  'connections.agent.ceiling': 'Ask the owner for anything over',
  'connections.agent.ceilingHint':
    'An amount in the business’s currency. Above it, even “Books on its own” asks first. Leave empty for no limit.',
  'connections.agent.ceilingInvalid': 'Write an amount, like 150 or 150.00.',
  'connections.agent.ownerNumber': 'Owner’s number',
  'connections.agent.ownerNumberHint':
    'Who it texts when it needs help. With the country code, like +1 340 555 0100.',
  'connections.agent.greeting': 'Greeting in chat and texts',
  'connections.agent.voiceGreeting': 'Greeting on the phone',
  'connections.agent.voiceGreetingHint': 'Keep it under about twelve words — it is spoken aloud.',
  'connections.agent.character': 'Its character, in one sentence',
  'connections.agent.formality': 'How formal',
  'connections.agent.formality.unset': 'Not chosen',
  'connections.agent.formality.casual': 'Casual',
  'connections.agent.formality.warm': 'Warm',
  'connections.agent.formality.professional': 'Professional',
  'connections.agent.voiceMode': 'On the phone',
  'connections.agent.voiceMode.disabled': 'No calls — chat and texts only',
  'connections.agent.voiceMode.ai_first': 'The agent answers',
  'connections.agent.voiceMode.owner_first': 'It rings the owner; the agent takes what they miss',
  'connections.agent.avoid': 'Words it never uses',
  'connections.agent.avoidHint': 'One per line.',
  'connections.agent.save': 'Save',
  'connections.agent.nothingChanged': 'Nothing changed.',
  'connections.agent.saved': 'Saved.',
  'connections.agent.summaryGreeting': ({ greeting }: { greeting: string }) =>
    `Greets with “${greeting}”`,

  /* ---- what the AI may book --------------------------------------- */
  'connections.book.title': 'What the AI may book',
  'connections.book.note':
    'Each switch decides whether the concierge may book that service by itself.',
  'connections.book.empty': 'It hasn’t been sent the services yet — use Sync now.',
  'connections.book.label': ({ service }: { service: string }) => `The AI may book ${service}`,
  'connections.book.retired': 'no longer in the service list',

  /* ---- partner access keys ---------------------------------------- */
  'connections.keys.title': 'Partner access keys',
  'connections.keys.chip': ({ count }: { count: number }) =>
    plural(count, '1 working', `${String(count)} working`),
  'connections.keys.none':
    'No partner can reach the business yet. Keys are made when a partner is connected.',
  'connections.keys.noneButConcierge':
    'No other partner can reach the business. The concierge’s key is in its card.',
  'connections.keys.conciergeElsewhere': 'The concierge’s key is shown in its card.',
  'connections.keys.make': 'Make a key…',
  'connections.keys.showRevoked': ({ count }: { count: number }) =>
    plural(count, 'Show 1 revoked', `Show ${String(count)} revoked`),
  'connections.keys.hideRevoked': 'Hide revoked',
  'connections.keys.made': ({ when }: { when: string }) => `made ${when}`,
  'connections.keys.used': ({ when }: { when: string }) => `used ${when}`,
  'connections.keys.neverUsed': 'never used',
  'connections.keys.revokedOn': ({ when }: { when: string }) => `revoked ${when}`,
  'connections.keys.env.test': 'test',
  'connections.keys.env.live': 'live',
  'connections.keys.mayDo': 'What it may do',
  'connections.keys.revoke': 'Revoke',
  'connections.keys.revokeTitle': ({ integration, made }: { integration: string; made: string }) =>
    `Revoke the ${integration} key made ${made}?`,
  'connections.keys.revokeConsequence':
    'Whoever holds this key stops getting in. It stops working at once here, and everywhere else within 15 seconds. A revoked key can’t be brought back — make another if it is still needed.',
  'connections.keys.revokeLabel': 'Revoke key',

  'connections.keys.dialog.title': 'Make a key',
  'connections.keys.dialog.who': 'Who it’s for',
  'connections.keys.dialog.whoHint':
    'The partner’s short name, as they gave it — the concierge is “aiconcierge”.',
  'connections.keys.dialog.env': 'Test or live',
  'connections.keys.dialog.env.test': 'Test',
  'connections.keys.dialog.env.test.what':
    'For a partner still trying things out. The key says “test” at the start, so it is easy to tell apart.',
  'connections.keys.dialog.env.live': 'Live',
  'connections.keys.dialog.env.live.what':
    'For a partner working for the business for real. Both reach the same business — the label is there so nobody mistakes one for the other.',
  'connections.keys.dialog.may': 'What it may do',
  'connections.keys.dialog.mayHint': 'Nothing is allowed unless it is ticked.',
  'connections.keys.dialog.turnOn': ({ feature }: { feature: string }) =>
    `Turn on ${feature} first`,
  'connections.keys.dialog.twoActive': ({ integration }: { integration: string }) =>
    `${integration} already holds two working keys. Each partner may hold two at a time, so they can switch to a new one before the old is revoked: revoke the one they no longer use, then make this one.`,
  'connections.keys.dialog.nothingTicked': 'Tick at least one thing it may do.',
  'connections.keys.dialog.make': 'Make the key',
  'connections.keys.dialog.making': 'Making…',
  'connections.keys.onceLabel': ({ integration, env }: { integration: string; env: string }) =>
    `The new ${env} key for ${integration}`,

  /* ---- scopes, in words ------------------------------------------- */
  'connections.scope.catalog:read': 'See the services and their prices',
  'connections.scope.calendar:read': 'See open times — never who is booked',
  'connections.scope.calendar:write': 'Hold, book, cancel and move appointments',
  'connections.scope.clients:read': 'Recognise returning clients and their visits',
  'connections.scope.clients:preferences':
    'Add preferences and notes to a client (these can touch on health)',
  'connections.scope.clients:write': 'Change a client’s details',
  'connections.scope.certificates:read': 'See gift certificate balances',
  'connections.scope.credit:read': 'See account credit balances',
  'connections.scope.products:read': 'See the products for sale',
  'connections.scope.menu:read': 'See the menu',
  'connections.scope.orders:read': 'See how an order is doing',
  'connections.scope.orders:write': 'Place an order',
  'connections.scope.departures:read': 'See upcoming trips and seats left',
  'connections.scope.lodging:read': 'See rooms, rates and stays',
  'connections.scope.payments:write': 'Send a payment link, priced by the business',

  /* ---- features, by the name the Setup tab gives them ------------- */

  /* ---- booking updates to partners ------------------------------- */
  'connections.updates.title': 'Booking updates to partners',
  'connections.updates.chip.none': 'Not set up',
  'connections.updates.chip.ok': 'All delivered',
  'connections.updates.chip.gaveUp': ({ count }: { count: number }) =>
    plural(count, '1 gave up', `${String(count)} gave up`),
  'connections.updates.chip.retrying': 'Retrying',
  'connections.updates.chip.failing': 'Failing',
  'connections.updates.none':
    'No partner receives booking updates yet. Every change is still recorded, so it can be sent once a partner is connected.',
  'connections.updates.ok': 'All updates delivered.',
  'connections.updates.gaveUp': ({
    count,
    since,
    more,
  }: {
    count: number;
    since: string;
    more: boolean;
  }) =>
    `${more ? `${String(count)} or more` : String(count)} ${plural(count, 'update', 'updates')} gave up since ${since}.`,
  'connections.updates.resend': 'Resend them',
  'connections.updates.retrying': ({ count }: { count: number }) =>
    plural(count, '1 update is being retried.', `${String(count)} updates are being retried.`),
  'connections.updates.failing':
    'An address stopped answering. Open History to resend from the time it went quiet.',
  'connections.updates.stopped': 'Sending is stopped for every address.',
  'connections.updates.addresses': 'Where updates go',
  'connections.updates.conciergeElsewhere': 'The concierge’s address is shown in its card.',
  'connections.updates.add': 'Add an address…',
  'connections.updates.edit': 'Change…',
  'connections.updates.stop': 'Stop sending…',
  'connections.updates.start': 'Start sending again',
  'connections.updates.started': ({ integration }: { integration: string }) =>
    `Sending to ${integration} again.`,
  'connections.updates.sends': ({ what }: { what: string }) => `Sends ${what}`,
  'connections.updates.everything': 'every booking change',
  'connections.updates.signedWith': ({ header }: { header: string }) =>
    `signed in the \`${header}\` header`,
  'connections.updates.state.stopped': 'Stopped',
  'connections.updates.state.failing': 'Failing',
  'connections.updates.stopTitle': ({ integration }: { integration: string }) =>
    `Stop sending booking updates to ${integration}?`,
  'connections.updates.stopConsequence': ({ url }: { url: string }) =>
    `Nothing is lost — every change is still recorded — but none is sent to ${url} until sending is started again. Resending from the time it stopped catches them up.`,
  'connections.updates.stopLabel': 'Stop sending',
  'connections.updates.stoppedDone': ({ integration }: { integration: string }) =>
    `Stopped sending to ${integration}.`,
  'connections.updates.resendTitle': ({ since }: { since: string }) =>
    `Resend every update since ${since}?`,
  'connections.updates.resendConsequence':
    'Every update since then is sent again with a fresh set of tries, and addresses marked as failing are given another chance — resending says you believe the partner is fixed. Partners ignore an update they already have, so resending more than was lost is safe; resending from far back is a burst of work of its own.',
  'connections.updates.resendLabel': 'Resend',
  'connections.updates.resent': ({ count, since }: { count: number; since: string }) =>
    `${plural(count, '1 update', `${String(count)} updates`)} since ${since} will be sent again.`,

  'connections.endpoint.titleNew': 'Add an address for booking updates',
  'connections.endpoint.titleEdit': ({ integration }: { integration: string }) =>
    `Where ${integration}’s updates go`,
  'connections.endpoint.who': 'Which partner',
  'connections.endpoint.whoHint': 'The partner’s short name, the same one its key was made for.',
  'connections.endpoint.url': 'Address',
  'connections.endpoint.urlHint':
    'Must start with https:// (plain http only on this computer). The signature proves an update came from us; it doesn’t hide a client’s name from the network.',
  'connections.endpoint.secret': 'Where the signing secret is stored',
  'connections.endpoint.secretHint':
    'A reference to the secret, never the secret — the name of where it is kept, like projects/…/secrets/…/versions/latest.',
  'connections.endpoint.secretLooksReal':
    'That looks like the secret itself. Paste the name of where it is kept instead — the secret never goes in this console.',
  'connections.endpoint.header': 'Signature header',
  'connections.endpoint.headerHint': 'The header the partner reads the signature from.',
  'connections.endpoint.events': 'Which updates',
  'connections.endpoint.events.all': 'Every booking change (recommended)',
  'connections.endpoint.events.some': 'Only these:',
  'connections.endpoint.events.pickOne': 'Tick at least one, or choose every booking change.',
  'connections.endpoint.save': 'Save',
  'connections.endpoint.saved': ({ integration, url }: { integration: string; url: string }) =>
    `Updates for ${integration} now go to ${url}.`,
  'connections.endpoint.overwriteTitle': ({ integration }: { integration: string }) =>
    `Replace where ${integration}’s updates go?`,
  'connections.endpoint.overwriteConsequence': ({
    before,
    after,
  }: {
    before: string;
    after: string;
  }) =>
    `From now on, updates for this partner go to ${after} instead of ${before}, signed with the secret you named. What it receives is replaced by what you ticked.`,
  'connections.endpoint.overwriteLabel': 'Replace',

  /* ---- events, in words ------------------------------------------- */
  'connections.event.booking.created': 'New booking',
  'connections.event.booking.updated': 'Booking changed',
  'connections.event.booking.cancelled': 'Booking cancelled',
  'connections.event.catalog.updated': 'Services or prices changed',
  'connections.event.profile.updated': 'Business details changed',
  'connections.event.hours.updated': 'Hours changed',
  'connections.event.client.updated': 'Client details changed',
  'connections.event.order.created': 'New order',
  'connections.event.order.updated': 'Order changed',
  'connections.event.reservation.created': 'New stay',
  'connections.event.reservation.updated': 'Stay changed',

  /* ---- history ---------------------------------------------------- */
  'connections.history.title': 'History',
  'connections.history.filter': 'Show',
  'connections.history.filter.all': 'All',
  'connections.history.status.queued': 'Waiting to send',
  'connections.history.status.delivering': 'Sending',
  'connections.history.status.delivered': 'Delivered',
  'connections.history.status.failed': 'Retrying',
  'connections.history.status.exhausted': 'Gave up',
  'connections.history.empty': 'Nothing sent or waiting yet.',
  'connections.history.emptyFiltered': 'Nothing like that.',
  'connections.history.to': ({ integration }: { integration: string }) => `to ${integration}`,
  'connections.history.tries': ({ count }: { count: number }) =>
    plural(count, '1 try', `${String(count)} tries`),
  'connections.history.details': 'Details',
  'connections.history.hide': 'Hide',
  'connections.history.sentTo': ({ url }: { url: string }) => `Sent to ${url}.`,
  'connections.history.deliveredAt': ({ when }: { when: string }) => `Delivered ${when}.`,
  'connections.history.nextTry': ({ when }: { when: string }) => `Next try ${when}.`,
  'connections.history.noMoreTries': 'No more tries — resend to try again.',
  'connections.history.answered': ({ code }: { code: string }) =>
    `The partner last answered with status ${code}.`,
  'connections.history.noAnswer': 'The partner never answered.',
  'connections.history.said': 'What went wrong, as reported:',
  'connections.history.ids': 'For a support ticket',
  'connections.history.noPayload':
    'What the update said isn’t shown here and never reaches this console: it can carry a client’s name.',
  'connections.history.resendSince': 'Resend everything since',
  'connections.history.resendSinceGo': 'Resend…',
};

export const connectionsEs: typeof connectionsEn = {
  'connections.fence':
    'Las conexiones son parte de la segunda etapa, las reservas en línea. El negocio no necesita ninguna para trabajar su día en la aplicación.',
  'connections.loading': 'el conserje',

  'connections.concierge.title': 'Conserje IA',
  'connections.concierge.chip.unavailable': 'No disponible aquí',
  'connections.concierge.chip.off': 'Sin conectar',
  'connections.concierge.chip.paused': 'En pausa',
  'connections.concierge.chip.broken': 'No llega a la agenda',
  'connections.concierge.chip.todo': ({ count }) =>
    plural(count, '1 pendiente', `${String(count)} pendientes`),
  'connections.concierge.chip.on': 'Contestando',
  'connections.concierge.chip.unknown': 'No respondió',
  'connections.concierge.unavailable':
    'Esta consola no está enlazada con el servicio del conserje, así que desde aquí no puede conectar a nadie. No le pasa nada al negocio.',
  'connections.concierge.unavailableWhat': 'Lo que le falta a este despliegue',
  'connections.concierge.unreachable': ({ message }) =>
    `El conserje no respondió (${message}). Lo que se ve aquí es solo de este lado.`,
  'connections.concierge.pitch': 'Contesta a sus clientes y reserva por ellos. Antes de activarlo:',
  'connections.concierge.todo.agree':
    'Quien lleva el negocio acepta que un asistente conteste en su nombre, y en qué número',
  'connections.concierge.todo.services': 'Elegir qué servicios puede reservar',
  'connections.concierge.todo.phone': 'Un número de teléfono para él',
  'connections.concierge.connect': 'Conectar el conserje…',
  'connections.concierge.connectTitle': ({ name }) => `¿Conectar el conserje a ${name}?`,
  'connections.concierge.connectConsequence':
    'Activa la libreta de clientes, la lista de servicios, la agenda y el acceso de socios si están apagados; crea para el conserje su propia clave y un secreto para firmar las actualizaciones, y se los entrega de máquina a máquina — ninguno pasa por este navegador; luego le envía los servicios y los datos del negocio. No reserva nada hasta que se elija qué servicios puede reservar.',
  'connections.concierge.connectLabel': 'Conectar',
  'connections.concierge.connected':
    'Conectado. Su clave y su secreto de firma viajaron de máquina a máquina — ninguno pasó por este navegador.',

  'connections.concierge.doing.on': ({ name }) =>
    `Contesta a los clientes de ${name} y reserva por ellos.`,
  'connections.concierge.doing.paused':
    'En pausa: sigue contestando y tomando mensajes, pero no reserva nada hasta que se reanude.',
  'connections.concierge.doing.broken':
    'No puede usar la clave que tiene, así que no ve horarios libres ni reserva. Renueve la clave y vuelva a sincronizar.',
  'connections.concierge.doing.brokenDetail': ({ detail }) => `Lo que dijo: ${detail}`,
  'connections.concierge.synced': ({ services, details }) =>
    `Servicios enviados por última vez ${services}; datos del negocio enviados por última vez ${details}.`,
  'connections.concierge.never': 'nunca',
  'connections.concierge.syncNote':
    'Normalmente no hace falta sincronizar a mano — un cambio en los servicios o en los datos del negocio le llega en segundos.',
  'connections.concierge.stillTodo': 'Pendiente',
  'connections.concierge.missing.provider_connection':
    'No puede usar la clave que tiene — renueve la clave y vuelva a sincronizar.',
  'connections.concierge.missing.catalog':
    'Todavía no hay nada reservable publicado — primero agregue servicios.',
  'connections.concierge.missing.phone_number':
    'Todavía no tiene número: el chat funciona, las llamadas y los mensajes de texto no.',
  'connections.concierge.missing.owner_number':
    'No tiene a quién escribir cuando necesita ayuda — ponga el número de quien lleva el negocio en «Lo que hace el agente».',
  'connections.concierge.openConsole': 'Abrir la consola del conserje',

  'connections.concierge.wiring': 'Su clave y su dirección de actualizaciones',
  'connections.concierge.itsKeys': 'Su clave',
  'connections.concierge.noKey':
    'No tiene ninguna clave que funcione — renueve la clave y vuelva a sincronizar.',
  'connections.concierge.updatesTo': 'Las actualizaciones de reservas van a',
  'connections.concierge.noAddress':
    'A ningún sitio todavía — renovar la clave y sincronizar la configura.',
  'connections.concierge.addressFailing':
    'Las actualizaciones hacia él están fallando — vea «Actualizaciones de reservas a socios».',
  'connections.concierge.addressStopped': 'El envío está detenido.',
  'connections.concierge.secretAt': 'Secreto de firma guardado en',
  'connections.concierge.siteKey': 'Clave del chat para el sitio web',
  'connections.concierge.siteKeyNote': 'Pública — va en el código del sitio. No es un secreto.',

  'connections.concierge.sync': 'Sincronizar ahora',
  'connections.concierge.synced.done': 'Enviado de nuevo.',
  'connections.concierge.pause': 'Pausar…',
  'connections.concierge.resume': 'Reanudar',
  'connections.concierge.resumed': 'Reanudado — vuelve a reservar.',
  'connections.concierge.pauseTitle': '¿Pausar el conserje?',
  'connections.concierge.pauseConsequence': ({ name }) =>
    `Sigue contestando a los clientes de ${name} y tomando mensajes, pero no reserva nada hasta que se reanude. Su clave y sus ajustes quedan como están.`,
  'connections.concierge.pauseLabel': 'Pausar',
  'connections.concierge.rekey': 'Renovar clave y sincronizar…',
  'connections.concierge.rekeyTitle': '¿Renovar la clave del conserje y sincronizar?',
  'connections.concierge.rekeyConsequence':
    'Se crea una clave nueva y se entrega de máquina a máquina. La anterior se revoca cuando el conserje puede usar la nueva — si no puede, la anterior se conserva y esta tarjeta lo indica. Los servicios y los datos del negocio se envían de nuevo. No se borra nada y los clientes no notan nada.',
  'connections.concierge.rekeyLabel': 'Renovar clave y sincronizar',
  'connections.concierge.revoke': 'Pausar y revocar…',
  'connections.concierge.revokeTitle': '¿Pausar el conserje y revocar su clave?',
  'connections.concierge.revokeConsequence':
    'Sigue contestando y tomando mensajes, pero no reserva nada. Su clave se revoca y el acceso de socios se apaga, así que ya no puede leer los servicios ni los horarios libres del negocio. No se borra nada — ni el número, ni las conversaciones, ni los ajustes — y al conectarlo de nuevo se crea una clave nueva.',
  'connections.concierge.revokeLabel': 'Pausar y revocar',
  'connections.concierge.revoked': 'En pausa, y su clave está revocada.',

  'connections.agent.title': 'Lo que hace el agente',
  'connections.agent.edit': 'Cambiar lo que hace el agente…',
  'connections.agent.note':
    'Lo que sabe — horario, precios, políticas — sale de la configuración del propio negocio y se cambia allí. Esto es solo cómo se comporta.',
  'connections.agent.autonomy': 'Cuánto puede hacer por su cuenta',
  'connections.agent.autonomy.conservative': 'Pregunta primero',
  'connections.agent.autonomy.conservative.what':
    'Contesta y reúne los detalles, pero no reserva: propone, y quien lleva el negocio confirma.',
  'connections.agent.autonomy.balanced': 'Reserva, y avisa de lo inusual',
  'connections.agent.autonomy.balanced.what':
    'Reserva cualquier servicio que tenga permitido, y pasa a quien lleva el negocio lo que sea a medida, con descuento o con tono de queja, con una respuesta ya redactada.',
  'connections.agent.autonomy.autonomous': 'Reserva por su cuenta',
  'connections.agent.autonomy.autonomous.what':
    'Reserva sin consultar antes, y solo pasa lo que sea a medida o supere el importe de abajo.',
  'connections.agent.autonomy.notSet': 'Todavía sin elegir.',
  'connections.agent.ceiling': 'Consultar para cualquier cosa de más de',
  'connections.agent.ceilingHint':
    'Un importe en la moneda del negocio. Por encima, incluso «Reserva por su cuenta» consulta primero. Déjelo vacío para no poner límite.',
  'connections.agent.ceilingInvalid': 'Escriba un importe, como 150 o 150.00.',
  'connections.agent.ownerNumber': 'Número de quien lleva el negocio',
  'connections.agent.ownerNumberHint':
    'A quién escribe cuando necesita ayuda. Con el código de país, como +1 340 555 0100.',
  'connections.agent.greeting': 'Saludo en el chat y los mensajes',
  'connections.agent.voiceGreeting': 'Saludo por teléfono',
  'connections.agent.voiceGreetingHint': 'Menos de unas doce palabras — se dice en voz alta.',
  'connections.agent.character': 'Su carácter, en una frase',
  'connections.agent.formality': 'Qué tan formal',
  'connections.agent.formality.unset': 'Sin elegir',
  'connections.agent.formality.casual': 'Informal',
  'connections.agent.formality.warm': 'Cálido',
  'connections.agent.formality.professional': 'Profesional',
  'connections.agent.voiceMode': 'Por teléfono',
  'connections.agent.voiceMode.disabled': 'Sin llamadas — solo chat y mensajes',
  'connections.agent.voiceMode.ai_first': 'Contesta el agente',
  'connections.agent.voiceMode.owner_first':
    'Suena primero para quien lleva el negocio; el agente toma las que no conteste',
  'connections.agent.avoid': 'Palabras que nunca usa',
  'connections.agent.avoidHint': 'Una por línea.',
  'connections.agent.save': 'Guardar',
  'connections.agent.nothingChanged': 'No cambió nada.',
  'connections.agent.saved': 'Guardado.',
  'connections.agent.summaryGreeting': ({ greeting }) => `Saluda con «${greeting}»`,

  'connections.book.title': 'Lo que la IA puede reservar',
  'connections.book.note':
    'Cada interruptor decide si el conserje puede reservar ese servicio por su cuenta.',
  'connections.book.empty': 'Todavía no ha recibido los servicios — use Sincronizar ahora.',
  'connections.book.label': ({ service }) => `La IA puede reservar ${service}`,
  'connections.book.retired': 'ya no está en la lista de servicios',

  'connections.keys.title': 'Claves de acceso de socios',
  'connections.keys.chip': ({ count }) => plural(count, '1 en uso', `${String(count)} en uso`),
  'connections.keys.none':
    'Ningún socio puede llegar al negocio todavía. Las claves se crean cuando se conecta un socio.',
  'connections.keys.noneButConcierge':
    'Ningún otro socio puede llegar al negocio. La clave del conserje está en su tarjeta.',
  'connections.keys.conciergeElsewhere': 'La clave del conserje se muestra en su tarjeta.',
  'connections.keys.make': 'Crear una clave…',
  'connections.keys.showRevoked': ({ count }) =>
    plural(count, 'Mostrar 1 revocada', `Mostrar ${String(count)} revocadas`),
  'connections.keys.hideRevoked': 'Ocultar revocadas',
  'connections.keys.made': ({ when }) => `creada ${when}`,
  'connections.keys.used': ({ when }) => `usada ${when}`,
  'connections.keys.neverUsed': 'nunca usada',
  'connections.keys.revokedOn': ({ when }) => `revocada ${when}`,
  'connections.keys.env.test': 'prueba',
  'connections.keys.env.live': 'real',
  'connections.keys.mayDo': 'Lo que puede hacer',
  'connections.keys.revoke': 'Revocar',
  'connections.keys.revokeTitle': ({ integration, made }) =>
    `¿Revocar la clave de ${integration} creada ${made}?`,
  'connections.keys.revokeConsequence':
    'Quien tenga esta clave deja de entrar. Deja de funcionar al instante aquí, y en todas partes en 15 segundos. Una clave revocada no se puede recuperar — cree otra si todavía hace falta.',
  'connections.keys.revokeLabel': 'Revocar la clave',

  'connections.keys.dialog.title': 'Crear una clave',
  'connections.keys.dialog.who': 'Para quién es',
  'connections.keys.dialog.whoHint':
    'El nombre corto del socio, tal como lo dio — el conserje es «aiconcierge».',
  'connections.keys.dialog.env': 'Prueba o real',
  'connections.keys.dialog.env.test': 'Prueba',
  'connections.keys.dialog.env.test.what':
    'Para un socio que todavía está probando. La clave dice «test» al principio, así que se distingue fácilmente.',
  'connections.keys.dialog.env.live': 'Real',
  'connections.keys.dialog.env.live.what':
    'Para un socio que trabaja para el negocio de verdad. Las dos llegan al mismo negocio — la etiqueta está para que nadie confunda una con la otra.',
  'connections.keys.dialog.may': 'Lo que puede hacer',
  'connections.keys.dialog.mayHint': 'No se permite nada que no esté marcado.',
  'connections.keys.dialog.turnOn': ({ feature }) => `Active primero ${feature}`,
  'connections.keys.dialog.twoActive': ({ integration }) =>
    `${integration} ya tiene dos claves en uso. Cada socio puede tener dos a la vez, para pasar a una nueva antes de revocar la anterior: revoque la que ya no usa y luego cree esta.`,
  'connections.keys.dialog.nothingTicked': 'Marque al menos una cosa que pueda hacer.',
  'connections.keys.dialog.make': 'Crear la clave',
  'connections.keys.dialog.making': 'Creando…',
  'connections.keys.onceLabel': ({ integration, env }) =>
    `La nueva clave de ${env} para ${integration}`,

  'connections.scope.catalog:read': 'Ver los servicios y sus precios',
  'connections.scope.calendar:read': 'Ver los horarios libres — nunca quién tiene cita',
  'connections.scope.calendar:write': 'Apartar, reservar, cancelar y mover citas',
  'connections.scope.clients:read': 'Reconocer a los clientes que vuelven y sus visitas',
  'connections.scope.clients:preferences':
    'Agregar preferencias y notas a un cliente (pueden tocar temas de salud)',
  'connections.scope.clients:write': 'Cambiar los datos de un cliente',
  'connections.scope.certificates:read': 'Ver los saldos de certificados de regalo',
  'connections.scope.credit:read': 'Ver los saldos de crédito de cuenta',
  'connections.scope.products:read': 'Ver los productos a la venta',
  'connections.scope.menu:read': 'Ver el menú',
  'connections.scope.orders:read': 'Ver cómo va un pedido',
  'connections.scope.orders:write': 'Hacer un pedido',
  'connections.scope.departures:read': 'Ver las próximas salidas y los asientos libres',
  'connections.scope.lodging:read': 'Ver habitaciones, tarifas y estancias',
  'connections.scope.payments:write': 'Enviar un enlace de pago, con el precio del negocio',

  'connections.updates.title': 'Actualizaciones de reservas a socios',
  'connections.updates.chip.none': 'Sin configurar',
  'connections.updates.chip.ok': 'Todo entregado',
  'connections.updates.chip.gaveUp': ({ count }) =>
    plural(count, '1 abandonada', `${String(count)} abandonadas`),
  'connections.updates.chip.retrying': 'Reintentando',
  'connections.updates.chip.failing': 'Fallando',
  'connections.updates.none':
    'Ningún socio recibe actualizaciones de reservas todavía. Cada cambio queda registrado igualmente, así que se puede enviar cuando se conecte un socio.',
  'connections.updates.ok': 'Todas las actualizaciones entregadas.',
  'connections.updates.gaveUp': ({ count, since, more }) =>
    `${more ? `${String(count)} o más` : String(count)} ${plural(count, 'actualización se abandonó', 'actualizaciones se abandonaron')} desde ${since}.`,
  'connections.updates.resend': 'Reenviarlas',
  'connections.updates.retrying': ({ count }) =>
    plural(
      count,
      '1 actualización se está reintentando.',
      `${String(count)} actualizaciones se están reintentando.`,
    ),
  'connections.updates.failing':
    'Una dirección dejó de contestar. Abra el historial para reenviar desde el momento en que se calló.',
  'connections.updates.stopped': 'El envío está detenido para todas las direcciones.',
  'connections.updates.addresses': 'Adónde van las actualizaciones',
  'connections.updates.conciergeElsewhere': 'La dirección del conserje se muestra en su tarjeta.',
  'connections.updates.add': 'Agregar una dirección…',
  'connections.updates.edit': 'Cambiar…',
  'connections.updates.stop': 'Dejar de enviar…',
  'connections.updates.start': 'Volver a enviar',
  'connections.updates.started': ({ integration }) => `Se vuelve a enviar a ${integration}.`,
  'connections.updates.sends': ({ what }) => `Envía ${what}`,
  'connections.updates.everything': 'cada cambio de reserva',
  'connections.updates.signedWith': ({ header }) => `firmado en la cabecera \`${header}\``,
  'connections.updates.state.stopped': 'Detenido',
  'connections.updates.state.failing': 'Fallando',
  'connections.updates.stopTitle': ({ integration }) =>
    `¿Dejar de enviar actualizaciones de reservas a ${integration}?`,
  'connections.updates.stopConsequence': ({ url }) =>
    `No se pierde nada — cada cambio queda registrado — pero no se envía nada a ${url} hasta que se vuelva a activar el envío. Reenviar desde el momento en que se detuvo las pone al día.`,
  'connections.updates.stopLabel': 'Dejar de enviar',
  'connections.updates.stoppedDone': ({ integration }) => `Se dejó de enviar a ${integration}.`,
  'connections.updates.resendTitle': ({ since }) =>
    `¿Reenviar todas las actualizaciones desde ${since}?`,
  'connections.updates.resendConsequence':
    'Cada actualización desde ese momento se envía de nuevo con intentos nuevos, y las direcciones marcadas como fallando tienen otra oportunidad — reenviar es decir que cree que el socio ya está arreglado. Los socios ignoran una actualización que ya tienen, así que reenviar de más es seguro; reenviar desde muy atrás es una carga de trabajo en sí.',
  'connections.updates.resendLabel': 'Reenviar',
  'connections.updates.resent': ({ count, since }) =>
    `${plural(count, '1 actualización', `${String(count)} actualizaciones`)} desde ${since} se enviarán de nuevo.`,

  'connections.endpoint.titleNew': 'Agregar una dirección para las actualizaciones de reservas',
  'connections.endpoint.titleEdit': ({ integration }) =>
    `Adónde van las actualizaciones de ${integration}`,
  'connections.endpoint.who': 'Qué socio',
  'connections.endpoint.whoHint':
    'El nombre corto del socio, el mismo para el que se creó su clave.',
  'connections.endpoint.url': 'Dirección',
  'connections.endpoint.urlHint':
    'Debe empezar por https:// (http solo en esta computadora). La firma prueba que una actualización salió de nosotros; no oculta el nombre de un cliente en la red.',
  'connections.endpoint.secret': 'Dónde está guardado el secreto de firma',
  'connections.endpoint.secretHint':
    'Una referencia al secreto, nunca el secreto — el nombre de dónde está guardado, como projects/…/secrets/…/versions/latest.',
  'connections.endpoint.secretLooksReal':
    'Eso parece el secreto en sí. Pegue el nombre de dónde está guardado — el secreto nunca entra en esta consola.',
  'connections.endpoint.header': 'Cabecera de la firma',
  'connections.endpoint.headerHint': 'La cabecera de la que el socio lee la firma.',
  'connections.endpoint.events': 'Qué actualizaciones',
  'connections.endpoint.events.all': 'Cada cambio de reserva (recomendado)',
  'connections.endpoint.events.some': 'Solo estas:',
  'connections.endpoint.events.pickOne': 'Marque al menos una, o elija cada cambio de reserva.',
  'connections.endpoint.save': 'Guardar',
  'connections.endpoint.saved': ({ integration, url }) =>
    `Las actualizaciones de ${integration} ahora van a ${url}.`,
  'connections.endpoint.overwriteTitle': ({ integration }) =>
    `¿Reemplazar adónde van las actualizaciones de ${integration}?`,
  'connections.endpoint.overwriteConsequence': ({ before, after }) =>
    `Desde ahora, las actualizaciones para este socio van a ${after} en lugar de ${before}, firmadas con el secreto que indicó. Lo que recibe se reemplaza por lo que marcó.`,
  'connections.endpoint.overwriteLabel': 'Reemplazar',

  'connections.event.booking.created': 'Reserva nueva',
  'connections.event.booking.updated': 'Reserva cambiada',
  'connections.event.booking.cancelled': 'Reserva cancelada',
  'connections.event.catalog.updated': 'Cambiaron servicios o precios',
  'connections.event.profile.updated': 'Cambiaron los datos del negocio',
  'connections.event.hours.updated': 'Cambió el horario',
  'connections.event.client.updated': 'Cambiaron los datos de un cliente',
  'connections.event.order.created': 'Pedido nuevo',
  'connections.event.order.updated': 'Pedido cambiado',
  'connections.event.reservation.created': 'Estancia nueva',
  'connections.event.reservation.updated': 'Estancia cambiada',

  'connections.history.title': 'Historial',
  'connections.history.filter': 'Mostrar',
  'connections.history.filter.all': 'Todo',
  'connections.history.status.queued': 'Esperando envío',
  'connections.history.status.delivering': 'Enviando',
  'connections.history.status.delivered': 'Entregada',
  'connections.history.status.failed': 'Reintentando',
  'connections.history.status.exhausted': 'Abandonada',
  'connections.history.empty': 'Nada enviado ni en espera todavía.',
  'connections.history.emptyFiltered': 'Nada de eso.',
  'connections.history.to': ({ integration }) => `a ${integration}`,
  'connections.history.tries': ({ count }) =>
    plural(count, '1 intento', `${String(count)} intentos`),
  'connections.history.details': 'Detalles',
  'connections.history.hide': 'Ocultar',
  'connections.history.sentTo': ({ url }) => `Enviada a ${url}.`,
  'connections.history.deliveredAt': ({ when }) => `Entregada ${when}.`,
  'connections.history.nextTry': ({ when }) => `Próximo intento ${when}.`,
  'connections.history.noMoreTries': 'No hay más intentos — reenvíe para intentarlo de nuevo.',
  'connections.history.answered': ({ code }) =>
    `El socio contestó por última vez con el estado ${code}.`,
  'connections.history.noAnswer': 'El socio nunca contestó.',
  'connections.history.said': 'Lo que falló, tal como se informó:',
  'connections.history.ids': 'Para un reporte de soporte',
  'connections.history.noPayload':
    'Lo que decía la actualización no se muestra aquí y nunca llega a esta consola: puede llevar el nombre de un cliente.',
  'connections.history.resendSince': 'Reenviar todo desde',
  'connections.history.resendSinceGo': 'Reenviar…',
};
